/**
 * Phase 6 — AI Closed-Loop Recommendations widget.
 *
 * Renders 3–5 proactive next-service suggestions tailored to the member.
 * Falls back gracefully when the LLM is unavailable.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles, ChevronRight, Loader2, Compass, Plane, Anchor, Home,
  Briefcase, Brush, Globe, Crown, MessageSquare, Wrench, TrendingUp,
  Calendar, Ship, Heart
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  concierge: { Icon: MessageSquare, path: '/concierge' },
  travel: { Icon: Compass, path: '/concierge' },
  property_management: { Icon: Home, path: '/concierge' },
  interior_design: { Icon: Brush, path: '/concierge' },
  renovation: { Icon: Wrench, path: '/concierge' },
  luxury_mobility: { Icon: Anchor, path: '/marketplace' },
  yacht_services: { Icon: Ship, path: '/marketplace' },
  charter_services: { Icon: Ship, path: '/concierge' },
  private_aviation: { Icon: Plane, path: '/concierge' },
  wealth_planning: { Icon: Briefcase, path: '/investor/insights' },
  investment_opportunities: { Icon: TrendingUp, path: '/investor/insights' },
  rentals: { Icon: Home, path: '/properties' },
  resale_assistance: { Icon: Home, path: '/concierge' },
  lifestyle_experiences: { Icon: Heart, path: '/concierge' },
  premium_memberships: { Icon: Crown, path: '/subscription' },
};

const PRIORITY_RING = {
  high: 'ring-gold/40 bg-gold/5',
  medium: 'ring-white/10 bg-white/[0.02]',
  low: 'ring-white/5 bg-white/[0.015]',
};

const AINextServices = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fallback, setFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/recommendations/next-services`);
        if (cancelled) return;
        setItems(data.recommendations || []);
        setFallback(!!data.fallback);
      } catch {
        // silent — section just stays hidden
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="glass-card p-6 flex items-center gap-3 text-text-secondary">
        <Loader2 className="w-4 h-4 animate-spin" /> Curating your next experiences…
      </div>
    );
  }
  if (!items.length) return null;

  return (
    <div className="glass-card p-6" data-testid="ai-next-services">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold" />
          <h3 className="font-playfair text-xl font-semibold">What's Next for You</h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-text-secondary">
          {fallback ? 'Curated picks' : 'AI-curated'} · refreshed today
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((r, i) => {
          const meta = SERVICE_META[r.service] || { Icon: Globe, path: '/dashboard' };
          const ring = PRIORITY_RING[r.priority] || PRIORITY_RING.medium;
          return (
            <motion.button
              key={`${r.service}-${i}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              onClick={() => navigate(meta.path)}
              className={`group text-left rounded-xl p-4 ring-1 ${ring} hover:ring-gold/50 transition-all`}
              data-testid={`ai-next-service-${r.service}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-gold/10 ring-1 ring-gold/20 flex items-center justify-center flex-shrink-0">
                  <meta.Icon className="w-4 h-4 text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h4 className="text-base font-semibold text-white">{r.headline}</h4>
                    {r.priority === 'high' && (
                      <span className="text-[9px] uppercase tracking-[0.2em] text-gold font-semibold">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/65 leading-relaxed mb-3">{r.reason}</p>
                  <span className="inline-flex items-center gap-1 text-xs text-gold uppercase tracking-[0.18em] font-semibold group-hover:gap-2 transition-all">
                    {r.cta_label} <ChevronRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default AINextServices;
